import { useNavigate } from 'react-router-dom'
import { Clock, Edit2, Link2 } from 'lucide-react'
import type { CalendarTask } from '../../api/calendar'
import { Modal } from '../ui/Modal'
import { fullName } from '../../lib/utils'
import { taskColor, formatTaskDateTime as formatDateTime } from '../../lib/taskColors'

/**
 * Expanded detail view for a calendar task.
 *
 * Lives here rather than in the Calendar page because the dashboard opens the
 * very same popup — the two must never drift apart.
 *
 * `onEdit` is optional: the calendar passes it, the dashboard does not (it has
 * no edit form), and the Edit button is simply absent there.
 */
export function EventDetailModal({ task, companyId, isOwner, onClose, onEdit }: {
  task: CalendarTask | null
  companyId: string
  isOwner: boolean
  onClose: () => void
  onEdit?: (t: CalendarTask) => void
}) {
  const navigate = useNavigate()
  if (!task) return null

  const color = taskColor(task.type)
  const timeLabel = task.isAllDay
    ? (task.dueDate ? new Date(task.dueDate).toLocaleDateString([], { dateStyle: 'full' }) : 'All day')
    : `${formatDateTime(task.startAt)} – ${formatDateTime(task.endAt)}`
  const createdAtLabel = new Date(task.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
  const linkedHref = task.refType && task.refId
    ? `/company/${companyId}/${task.refType === 'contact' ? 'contacts' : task.refType + 's'}/${task.refId}`
    : null

  return (
    <Modal open={!!task} onClose={onClose} title="Event details" size="lg">
      <div className="p-4 space-y-4">
        <div>
          <span
            className="inline-block text-xs font-medium px-2 py-0.5 rounded-full text-white mb-2"
            style={{ backgroundColor: color }}
          >
            {task.type === 'meeting' ? 'Meeting' : 'Reminder'}
          </span>
          <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{timeLabel}</span>
          </div>
          {task.assignedUser && (
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0 opacity-0" />
              <span>Assigned: {fullName(task.assignedUser.firstName, task.assignedUser.lastName)}</span>
            </div>
          )}
          {linkedHref && task.linkedRecordName && (
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => { onClose(); navigate(linkedHref) }}
                className="text-primary-600 hover:underline text-left"
              >
                <span className="capitalize text-gray-400 mr-1">{task.refType}:</span>
                {task.linkedRecordName}
              </button>
            </div>
          )}
        </div>

        {task.description && (
          <div>
            <label className="form-label">Description</label>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{task.description}</p>
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 text-xs text-gray-400 space-y-1">
          <div>Created {createdAtLabel}</div>
          {isOwner && task.createdByUser && (
            <div>By {fullName(task.createdByUser.firstName, task.createdByUser.lastName)}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
          {onEdit && (
            <button onClick={() => onEdit(task)} className="btn btn-primary flex items-center gap-1.5">
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
